import axios from 'axios';

const RAW_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// normalize: remove trailing slash
const API_ORIGIN = RAW_API_URL.replace(/\/$/, '');

// always talk to the API under /api
const API_BASE_URL = `${API_ORIGIN}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh and errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't tried refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await api.post('/auth/refresh', { refreshToken });

          const { accessToken } = response.data;
          localStorage.setItem('accessToken', accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // If 403 with NO_ACCESS code, redirect to subscription expired page
    if (error.response?.status === 403 && error.response?.data?.code === 'NO_ACCESS') {
      sessionStorage.setItem('subscriptionExpired', 'true');
      window.location.href = '/subscription-expired';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (name, email, phone, password) => api.post("/auth/register", { name, email, phone, password, role: "caregiver" }),
  logout: (refreshToken) => api.post("/auth/logout", { refreshToken }),
  refresh: (refreshToken) => api.post("/auth/refresh", { refreshToken }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) => api.post("/auth/reset-password", { token, newPassword }),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerificationEmail: (email) => api.post('/signup/resend-verification-email', { email }),
  requestEmailChange: (data) => api.post('/auth/request-email-change', data),
  verifyEmailChange: (code) => api.post('/auth/verify-email-change', { code }),
  resumeCheckout: (email) => axios.post(`${API_ORIGIN}/api/signup/resume-checkout`, { email }),
};

// User API calls
export const userAPI = {
  // Profile
  getMe: () => api.get('/users/me'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.post('/users/change-password', data),
  
  // Subscription
  getSubscription: () => api.get('/users/subscription'),
  createBillingPortalSession: () => api.post('/users/billing-portal'),
  createResubscribeSession: () => api.post('/users/resubscribe'),
  
  // Notification Preferences
  updateNotificationPreferences: (data) => api.put('/users/notification-preferences', data),
  
  // Client management
  checkClientPhone: (phone) => api.post('/users/check-client-phone', { phone }),
  checkClientEmail: (email) => api.post('/users/check-client-email', { email }),
  getClients: () => api.get('/users/clients'),
  addClient: (data) => api.post('/users/clients', data),
  removeClient: (clientId, data) => api.delete(`/users/clients/${clientId}`, { data }),
  updateClient: (clientId, data) => api.put(`/users/clients/${clientId}`, data),

  // Self-enrollment
  addSelf: (data) => api.post('/users/add-self', data),
  removeSelf: (data) => api.post('/users/remove-self', data),

  // Guide
  sendGuide: (clientId) => api.post(`/users/clients/${clientId}/send-guide`),

  // Verifications
  resendVerification: (verificationId) => api.post(`/users/clients/${verificationId}/resend`),
  getVerificationStatus: (verificationId) => api.get(`/users/clients/verification/${verificationId}`),
  cancelVerification: (verificationId, data) => api.delete(`/users/clients/verification/${verificationId}`, data ? { data } : undefined),

  // Feedback
  submitFeedback: (formData) => api.post('/users/feedback', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getMyFeedback: (params) => api.post('/users/feedback/history', params),
  getFeedbackAttachmentUrl: (feedbackId, attachmentKey) =>
    api.post('/users/feedback/attachment-url', { feedbackId, attachmentKey }),
};

// Reporting API calls
export const reportingAPI = {
  getStats: () => api.get('/reporting/stats'),
  getClientsStats: () => api.get('/reporting/clients-stats'),
  getHistory: (params) => api.get('/reporting/history', { params }),
  getMessage: (messageId) => api.get(`/reporting/message/${messageId}`),
  updateMessageStatus: (messageId, updates) => api.patch(`/reporting/message/${messageId}`, updates),
  
  // Notes
  getNotes: (messageId) => api.get(`/reporting/message/${messageId}/notes`),
  addNote: (messageId, noteText) => api.post(`/reporting/message/${messageId}/notes`, { note_text: noteText }),
  updateNote: (messageId, noteId, noteText) => api.put(`/reporting/message/${messageId}/notes/${noteId}`, { note_text: noteText }),
  deleteNote: (messageId, noteId) => api.delete(`/reporting/message/${messageId}/notes/${noteId}`),
  
  // Feedback
  submitFeedback: (messageId, feedback, notes = null) =>
    api.patch(`/reporting/message/${messageId}/feedback`, { feedback, notes }),
  removeFeedback: (messageId) =>
    api.delete(`/reporting/message/${messageId}/feedback`),

  // Money sent
  acknowledgeMoneySent: (messageId) =>
    api.patch(`/reporting/message/${messageId}/acknowledge-money-sent`),

  // PII disclosed
  acknowledgePiiDisclosed: (messageId) =>
    api.patch(`/reporting/message/${messageId}/acknowledge-pii-disclosed`),
};

// Contact Change API calls (email/phone verification)
export const contactAPI = {
  // Email change
  requestEmailChange: (newEmail) => api.post('/contact/request-email-change', { newEmail }),
  verifyEmailChange: (token) => api.post('/contact/verify-email-change', { token }),
  
  // Phone change
  requestPhoneChange: (newPhone) => api.post('/contact/request-phone-change', { newPhone }),
  verifyPhoneChange: (code) => api.post('/contact/verify-phone-change', { code }),
  
  // General
  getPendingChanges: () => api.get('/contact/pending-changes'),
  resendVerification: (type) => api.post('/contact/resend-verification', { type }),
  cancelChange: (type) => api.delete(`/contact/pending-change/${type}`),
};

// Release Notes API calls
export const releasesAPI = {
  getAll: () => api.get('/releases'),
  getLatest: () => api.get('/releases/latest'),
  markSeen: (version) => api.post('/releases/seen', { version }),
};

// Training API calls
export const trainingAPI = {
  // Check if training system is enabled
  getStatus: () => api.get('/training/status'),

  // List available training scenarios
  listScenarios: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.channel) params.append('channel', filters.channel);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.risk_level) params.append('risk_level', filters.risk_level);
    return api.get(`/training/scenarios?${params}`);
  },
  
  // Create a new training session
  createSession: (data) => api.post('/training/sessions', data),
  
  // Send a training message
  sendSession: (sessionId) => api.post(`/training/sessions/${sessionId}/send`),
  
  // Record response to training
  respondToSession: (sessionId, data) => api.post(`/training/sessions/${sessionId}/respond`, data),
  
  // Get training stats for a client
  getClientStats: (clientId) => api.get(`/training/clients/${clientId}/stats`),

  // Get training history for a client
  getClientHistory: (clientId, limit = 20) => api.get(`/training/clients/${clientId}/history?limit=${limit}`),

  // Update training schedule for a client
  updateSchedule: (clientId, settings) => api.put(`/training/clients/${clientId}/schedule`, settings),
  
  // Request on-demand training
  requestTraining: (channel = 'sms') => api.post('/training/request', { channel }),

  // Self-training (practice in dashboard)
  startPractice: (data = {}) => api.post('/training/practice', data),
  answerPractice: (sessionId, data) => api.post(`/training/practice/${sessionId}/answer`, data),
  getMyStats: () => api.get('/training/my/stats'),
  getMyHistory: (limit = 20) => api.get(`/training/my/history?limit=${limit}`),

  // Scheduled Training
  getScheduledSettings: () => api.get('/users/training-settings'),
  updateScheduledSettings: (data) => api.put('/users/training-settings', data),
  getScheduledSchedule: () => api.get('/users/training-schedule'),
  getScheduledHistory: (limit = 20) => api.get(`/users/training-send-history?limit=${limit}`),
};

// Safe List API calls
export const safelistAPI = {
  // List all safe list entries
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.client_id !== undefined) queryParams.append('client_id', params.client_id);
    if (params.contact_type) queryParams.append('contact_type', params.contact_type);
    if (params.limit) queryParams.append('limit', params.limit);
    return api.get(`/safelist?${queryParams}`);
  },
  
  // Get counts by type
  getCounts: () => api.get('/safelist/count'),
  
  // Search safe list
  search: (query) => api.get(`/safelist/search?q=${encodeURIComponent(query)}`),
  
  // Add entry to safe list
  add: (data) => api.post('/safelist', data),
  
  // Update an entry
  update: (entryId, data) => api.put(`/safelist/${entryId}`, data),
  
  // Remove entry
  remove: (entryId) => api.delete(`/safelist/${entryId}`),
  
  // Bulk add common trusted senders
  bulkAddCommon: () => api.post('/safelist/bulk-add-common'),
};

export default api;
